import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    LineElement,
    PointElement,
    Filler,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import projectQueries from "../../../../requests/queries/ProjectsQuesries";
import "./projectStats.css";

// Enregistrer les composants Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    LineElement,
    PointElement,
    Filler
);

export default function ProjectStats() {
    const { id } = useParams();
    const [chartsData, setChartsData] = useState(null);
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const [charts, proj] = await Promise.all([
                    projectQueries.getProjectCharts(id),
                    projectQueries.getProject(id)
                ]);
                setChartsData(charts);
                setProject(proj);
            } catch (err) {
                console.error("Erreur lors du chargement:", err);
                setError("Impossible de charger les statistiques");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    if (loading) {
        return (
            <div className="stats-page">
                <div className="stats-loading">
                    <div className="loading-spinner"></div>
                    <p>Chargement des statistiques...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="stats-page">
                <div className="stats-error">
                    <span className="error-icon">⚠️</span>
                    <p>{error}</p>
                    <Link to={`/project/${id}`} className="back-link">
                        ← Retour au projet
                    </Link>
                </div>
            </div>
        );
    }

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                labels: {
                    color: '#e5e7eb',
                    font: {
                        family: "'Outfit', sans-serif",
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                titleFont: { family: "'Outfit', sans-serif" },
                bodyFont: { family: "'Outfit', sans-serif" },
                borderColor: 'rgba(99, 102, 241, 0.3)',
                borderWidth: 1,
            }
        },
        scales: {
            x: {
                ticks: { color: '#9ca3af' },
                grid: { color: 'rgba(75, 85, 99, 0.3)' }
            },
            y: {
                ticks: { color: '#9ca3af' },
                grid: { color: 'rgba(75, 85, 99, 0.3)' }
            }
        }
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    color: '#e5e7eb',
                    font: {
                        family: "'Outfit', sans-serif",
                    },
                    padding: 15,
                }
            },
            tooltip: {
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                titleFont: { family: "'Outfit', sans-serif" },
                bodyFont: { family: "'Outfit', sans-serif" },
            }
        }
    };

    // Préparer les données pour le graphique temporel
    const timeChartData = {
        labels: chartsData?.eventsOverTime?.daily?.map(d => d.date) || [],
        datasets: [{
            label: 'Événements',
            data: chartsData?.eventsOverTime?.daily?.map(d => d.count) || [],
            fill: true,
            backgroundColor: 'rgba(99, 102, 241, 0.2)',
            borderColor: '#6366f1',
            tension: 0.4,
            pointBackgroundColor: '#6366f1',
        }]
    };

    // Préparer les données pour le graphique par heure
    const hourlyChartData = {
        labels: Array.from({ length: 24 }, (_, i) => `${i}h`),
        datasets: [{
            label: 'Activité par heure',
            data: chartsData?.eventsOverTime?.hourly?.map(h => h.count) || [],
            backgroundColor: 'rgba(34, 211, 238, 0.7)',
            borderColor: '#22d3ee',
            borderWidth: 1,
        }]
    };

    return (
        <div className="stats-page">
            <div className="stats-container">
                {/* Header */}
                <div className="stats-header">
                    <div className="stats-header-left">
                        <Link to={`/project/${id}`} className="back-btn">
                            ← Retour
                        </Link>
                        <h1>Statistiques</h1>
                        {project && <span className="project-name">{project.name}</span>}
                    </div>
                    <div className="stats-header-right">
                        <button 
                            className="export-btn"
                            onClick={() => projectQueries.downloadProjectLogsCsv(id, project?.name)}
                        >
                            📋 Logs bruts
                        </button>
                        <button 
                            className="export-btn stats"
                            onClick={() => projectQueries.downloadProjectStatsCsv(id, project?.name)}
                        >
                            📈 Stats CSV
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="stats-summary">
                    <div className="summary-card">
                        <span className="summary-icon">📊</span>
                        <div className="summary-content">
                            <span className="summary-value">{chartsData?.summary?.totalEvents || 0}</span>
                            <span className="summary-label">Événements totaux</span>
                        </div>
                    </div>
                    <div className="summary-card">
                        <span className="summary-icon">📄</span>
                        <div className="summary-content">
                            <span className="summary-value">{chartsData?.summary?.uniquePages || 0}</span>
                            <span className="summary-label">Pages actives</span>
                        </div>
                    </div>
                    <div className="summary-card">
                        <span className="summary-icon">📱</span>
                        <div className="summary-content">
                            <span className="summary-value">{chartsData?.summary?.uniqueDevices || 0}</span>
                            <span className="summary-label">Devices uniques</span>
                        </div>
                    </div>
                    <div className="summary-card">
                        <span className="summary-icon">🏷️</span>
                        <div className="summary-content">
                            <span className="summary-value">{chartsData?.summary?.uniqueTypes || 0}</span>
                            <span className="summary-label">Types d'actions</span>
                        </div>
                    </div>
                </div>

                {/* Charts Grid */}
                <div className="charts-grid">
                    {/* Events Over Time */}
                    <div className="chart-card large">
                        <h3>📈 Évolution temporelle</h3>
                        <div className="chart-wrapper">
                            {chartsData?.eventsOverTime?.daily?.length > 0 ? (
                                <Line data={timeChartData} options={chartOptions} />
                            ) : (
                                <div className="no-data">Aucune donnée temporelle</div>
                            )}
                        </div>
                    </div>

                    {/* Events by Type - Doughnut */}
                    <div className="chart-card">
                        <h3>🎯 Par type d'événement</h3>
                        <div className="chart-wrapper doughnut">
                            {chartsData?.eventsByType?.labels?.length > 0 ? (
                                <Doughnut data={chartsData.eventsByType} options={doughnutOptions} />
                            ) : (
                                <div className="no-data">Aucune donnée</div>
                            )}
                        </div>
                    </div>

                    {/* Events by Page - Bar */}
                    <div className="chart-card">
                        <h3>📄 Par page</h3>
                        <div className="chart-wrapper">
                            {chartsData?.eventsByPage?.labels?.length > 0 ? (
                                <Bar data={chartsData.eventsByPage} options={chartOptions} />
                            ) : (
                                <div className="no-data">Aucune donnée</div>
                            )}
                        </div>
                    </div>

                    {/* Events by Device - Bar */}
                    <div className="chart-card">
                        <h3>📱 Par device</h3>
                        <div className="chart-wrapper">
                            {chartsData?.eventsByDevice?.labels?.length > 0 ? (
                                <Bar data={chartsData.eventsByDevice} options={chartOptions} />
                            ) : (
                                <div className="no-data">Aucune donnée</div>
                            )}
                        </div>
                    </div>

                    {/* Hourly Activity */}
                    <div className="chart-card large">
                        <h3>🕐 Activité par heure</h3>
                        <div className="chart-wrapper">
                            <Bar data={hourlyChartData} options={chartOptions} />
                        </div>
                    </div>
                </div>

                {/* Top Pages & Devices Tables */}
                <div className="tables-grid">
                    <div className="data-table-card">
                        <h3>🏆 Top Pages</h3>
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Page</th>
                                        <th>Interactions</th>
                                        <th>%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chartsData?.topPages?.length > 0 ? (
                                        chartsData.topPages.map((page, idx) => (
                                            <tr key={idx}>
                                                <td className="rank">{idx + 1}</td>
                                                <td className="name">{page.name}</td>
                                                <td className="count">{page.count}</td>
                                                <td className="percent">
                                                    <div className="percent-bar">
                                                        <div 
                                                            className="percent-fill" 
                                                            style={{ width: `${page.percentage}%` }}
                                                        ></div>
                                                        <span>{page.percentage}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="4" className="no-data">Aucune donnée</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="data-table-card">
                        <h3>📱 Top Devices</h3>
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Device</th>
                                        <th>Interactions</th>
                                        <th>%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {chartsData?.topDevices?.length > 0 ? (
                                        chartsData.topDevices.map((device, idx) => (
                                            <tr key={idx}>
                                                <td className="rank">{idx + 1}</td>
                                                <td className="name">{device.name}</td>
                                                <td className="count">{device.count}</td>
                                                <td className="percent">
                                                    <div className="percent-bar">
                                                        <div 
                                                            className="percent-fill device" 
                                                            style={{ width: `${device.percentage}%` }}
                                                        ></div>
                                                        <span>{device.percentage}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="4" className="no-data">Aucune donnée</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

